/**
 * Модель бэклога на стороне UI: статусы, типы, приоритеты, этапы.
 *
 * Значения ДОЛЖНЫ совпадать с моделью v2 (навык /backlog §1–§4) и с CHECK-ами
 * схемы. Дублирование здесь неизбежно — браузер не читает YAML навыка, — поэтому
 * оно собрано в одном файле: разъехаться может один список, а не десять мест
 * по коду.
 *
 * Связанные файлы:
 *   - services/backlog-api/internal/domain/backlog.go — та же модель на сервере;
 *   - .claude/skills/backlog/SKILL.md — источник модели.
 */

/** Столбцы доски в порядке движения работы слева направо. */
export const COLUMNS = [
  { key: 'backlog', title: 'Бэклог', hint: 'Заведено, в работу не взято' },
  { key: 'in_progress', title: 'В работе', hint: 'Взята исполнителем' },
  { key: 'rework', title: 'Доработка', hint: 'Ревью вернуло' },
  { key: 'in_review', title: 'На ревью', hint: 'Код готов, ждёт проверки' },
  { key: 'dev_done', title: 'Готово к приёмке', hint: 'Ревью пройдено' },
  { key: 'done', title: 'Закрыто', hint: 'Критерии проверены на работающей системе' },
  { key: 'clarification', title: 'Уточнение', hint: 'Ждёт ответа постановщика' },
  { key: 'blocked', title: 'Заблокировано', hint: 'Нужен blocked_by' },
  { key: 'other', title: 'Прочее', hint: 'Нужен status_custom' },
]

export const TYPES = [
  { key: 'feature', title: 'Фича' },
  { key: 'bug', title: 'Баг' },
  { key: 'tech-debt', title: 'Техдолг' },
  { key: 'support', title: 'Обращение' },
  { key: 'incident', title: 'Инцидент' },
]

export const PRIORITIES = ['P0', 'P1', 'P2', 'P3']

/** Обязательные этапы для feature и bug (completion-gate, навык §4). */
export const REQUIRED_STAGES = ['spec', 'development', 'review', 'qa', 'validate']

export const STAGE_STATUSES = ['pending', 'passed', 'failed', 'skipped']

/** Статусы, требующие записанного session-context (навык §5). */
export const CONTEXT_REQUIRED = ['in_review', 'dev_done', 'blocked', 'clarification']

const PRIORITY_COLORS = { P0: '#e5534b', P1: '#e0a33e', P2: '#4d8bf0', P3: '#7d8590' }

/** Цвет метки приоритета. */
export function priorityColor(priority) {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS.P3
}

/**
 * Проверяет переход локально, до обращения к серверу.
 *
 * Нужна не вместо серверной проверки, а до неё: пользователь получает отказ
 * мгновенно и с объяснением, вместо того чтобы увидеть, как карточка уезжает
 * в столбец и возвращается обратно. Сервер всё равно проверяет заново — здесь
 * подсказка, там гарантия.
 *
 * @param {object} item — карточка
 * @param {string} next — целевой статус
 * @returns {string|null} причина отказа либо null
 */
export function transitionBlocker(item, next) {
  if (next === item.status) return null
  if (CONTEXT_REQUIRED.includes(next) && !item.has_session_context) {
    return 'Нужен session-context: без него карточку нельзя продолжить в другой сессии'
  }
  if (next === 'done' && ['feature', 'bug'].includes(item.type)) {
    const closed = (item.stages || [])
      .filter((s) => ['passed', 'failed', 'skipped'].includes(s.status))
      .map((s) => s.key)
    const missing = REQUIRED_STAGES.filter((s) => !closed.includes(s))
    if (missing.length) return `Не закрыты обязательные этапы: ${missing.join(', ')}`
  }
  if (next === 'blocked' && !item.blocked_by) {
    return 'Нужна причина блокировки (blocked_by)'
  }
  if (next === 'other' && !item.status_custom) {
    return 'Нужно собственное название статуса (status_custom)'
  }
  return null
}
